import { useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { getCleanedUrlParams, showError } from '@utils';
import { useInfiniteQuery } from 'react-query';
import { QUERY_KEYS } from 'constants/queryKeys';
import { driversServices } from '@services';
import { DRIVERS_TABS, LIMIT } from '@constants';
import { handleInspectionCars } from './utils';

const pageSize = LIMIT;

function getNextPageParam(lastPage, allPages) {
  const total = lastPage?.meta?.total || 0;
  const loaded = allPages.reduce(
    (acc, page) => acc + (page?.data?.length || 0),
    0
  );
  return loaded < total ? allPages.length + 1 : undefined;
}

export const useInspectionCarsOperations = () => {
  const router = useRouter();
  const {
    query: {
      location,
      type,
      query,
      showNotInspected,
      dateFrom,
      dateTo,
      assignee,
    },
    isReady,
  } = router;

  const getParams = useCallback(
    (pageParam = 1) => {
      const params = getCleanedUrlParams({
        page: pageParam,
        pageSize,
        order: 'desc',
        orderBy: `inspectionDate`,
        location,
        type: type >= 0 ? Number(type) : undefined,
        query,
        showInspected: showNotInspected,
        dateFrom,
        dateTo,
        assignee,
      });

      if (params.assignee) {
        if (typeof params.assignee === 'string') {
          params.assignee = params.assignee === 'none' ? '' : params.assignee;
        }
        if (Array.isArray(params.assignee)) {
          params.assignee = params.assignee.map((a) => (a === 'none' ? '' : a));
        }
      }

      return params;
    },
    [location, type, query, showNotInspected, dateFrom, dateTo, assignee]
  );

  const inspectionCarsResponse = useInfiniteQuery(
    [
      QUERY_KEYS.GET_INSPECTION_CARS_KEY,
      location,
      type,
      query,
      showNotInspected,
      dateFrom,
      dateTo,
      assignee,
    ],
    ({ pageParam = 1 }) =>
      driversServices?.getInspectedCars({
        ...getParams(pageParam),
        searchFor: DRIVERS_TABS.inspections,
      }),
    {
      enabled: isReady,
      getNextPageParam,
    }
  );

  const vehicles = useMemo(
    () =>
      inspectionCarsResponse?.data
        ? inspectionCarsResponse.data.pages.flatMap((page) => page?.data || [])
        : [],
    [inspectionCarsResponse?.data]
  );
  const groups = useMemo(
    () => handleInspectionCars(vehicles, 'inspectionDate'),
    [vehicles]
  );

  const fetchMore = () => {
    if (inspectionCarsResponse.hasNextPage) {
      inspectionCarsResponse.fetchNextPage();
    }
  };

  useEffect(() => {
    if (inspectionCarsResponse?.isError) {
      showError(inspectionCarsResponse?.error);
    }
  }, [inspectionCarsResponse?.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  const loading =
    inspectionCarsResponse.isFetching ||
    inspectionCarsResponse.isFetchingNextPage;

  const total = inspectionCarsResponse.data?.pages?.[0]?.meta?.total || 0;
  const { hasNextPage } = inspectionCarsResponse;

  return {
    total,
    loading,
    groups,
    fetchMore,
    hasNextPage,
  };
};
